<template>
    <div v-if="config" class="w-full flex flex-col items-start relative my-1">
        <div class="w-full mt-1 flex">
            <div :class="[hideLateralDescriptionValue ? 'w-full' : 'w-1/2 pr-2', inputClass]">
                <Label :for="config.display_name || config.name" :class="['mb-2', overrideLabelOpacity ? 'override-opacity' : '']">
                    {{ config.display_name || config.name }}
                </Label>
                <div class="flex items-center relative">
                    <Input
                        v-model="controlValue"
                        :id="config.display_name || config.name"
                        :name="config.display_name || config.name"
                        type="number"
                        :min="config.value?.min ? Number(config.value.min) : undefined"
                        :max="config.value?.max ? Number(config.value.max) : undefined"
                        :readonly="!editMode || config.readonly || config.value?.readonly"
                        :disabled="!editMode"
                        autocomplete="off"
                        :class="`w-full ${config.description && hideLateralDescriptionValue ? 'pr-8' : ''}`"
                        :aria-invalid="errors.length > 0" />
                    <Button
                        v-if="config.readonly || config.value?.readonly"
                        variant="outline"
                        size="sm"
                        class="ml-2"
                        @click.prevent.stop="copyToClipboard(controlValue)">
                        <Check v-if="copyDone" class="h-4 w-4" />
                        <Copy v-else class="h-4 w-4" />
                    </Button>
                    <Tooltip v-if="config.description && hideLateralDescriptionValue">
                        <TooltipTrigger asChild>
                            <span class="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center">
                                <Info class="h-4 w-4 text-muted-foreground" />
                            </span>
                        </TooltipTrigger>
                        <TooltipContent side="right" class="whitespace-normal max-w-xs">
                            <p class="text-xs" v-html="sanitizeHtml(config.description)" />
                        </TooltipContent>
                    </Tooltip>
                </div>
                <div v-if="errors.length" class="text-destructive mt-1 text-xs">
                    <template v-for="error in errors" :key="error.$uid">
                        <span>* {{ error.$message }}</span>
                    </template>
                </div>
            </div>
            <div v-if="!hideLateralDescriptionValue" class="w-1/2 pl-2">
                <Alert
                    v-if="config.description"
                    variant="default"
                    class="min-h-[calc(theme(spacing.10)+theme(spacing.6))] flex items-center p-3 text-xs border bg-muted/10">
                    <AlertDescription class="text-muted-foreground text-2xs">
                        <span v-html="sanitizeHtml(config.description)" />
                    </AlertDescription>
                </Alert>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onUpdated, onUnmounted } from 'vue';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, Copy, Check } from 'lucide-vue-next';
import _ from 'lodash';
import { sanitizeHtml } from '@/utils/utils';

const emit = defineEmits(['input']);
const props = defineProps({
    config: {
        type: Object,
        required: true
    },
    value: {
        type: [Number, String, null],
        default: null
    },
    editMode: {
        type: Boolean,
        default: true
    },
    overrideLabelOpacity: {
        type: Boolean,
        default: false
    },
    hideLateralDescription: {
        type: Boolean,
        default: false
    },
    errors: {
        type: Array,
        default: () => []
    },
    inputClass: {
        type: String,
        default: ''
    }
});

const controlValue = ref(_.isNumber(props.value) ? props.value : props.value || props.config.value.default);
const hideLateralDescriptionValue = ref(props.hideLateralDescription);
const copyDone = ref(false);

onUpdated(() => {
    hideLateralDescriptionValue.value = props.hideLateralDescription ? props.hideLateralDescription : !props.editMode || window.innerWidth <= 1024;
});

const handleResize = () => {
    hideLateralDescriptionValue.value = props.hideLateralDescription ? props.hideLateralDescription : window.innerWidth <= 1024;
};

function copyToClipboard(textToCopy) {
    copyDone.value = true;
    navigator.clipboard
        .writeText(textToCopy && typeof textToCopy === 'object' ? JSON.stringify(textToCopy, null, 2) : textToCopy)
        .then(() => setTimeout(() => (copyDone.value = false), 1000))
        .catch((err) => {
            console.error('Could not copy text: ', err);
        });
}

onMounted(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

watch(controlValue, (newValue) => {
    emit('input', newValue);
});

watch(
    () => props.value,
    (newValue) => {
        controlValue.value = newValue;
    }
);
</script>
